/**
 * Report Studio: design a pipeline report in one window, with the report in view the whole time.
 *
 * The window has three bands and no scrolling in the first two:
 *
 *   header   the report's name, its size, and Print / Excel / PDF
 *   deck     four panels side by side — Columns, Filters, Totals & calculations, Style & paper.
 *            Nothing to step through: every control that shapes the report is on screen at once.
 *   report   the live report across the bottom, laid out as it will print
 *
 * When the design is done, Finish asks where the report goes — private, shared with a colleague,
 * or sent on a schedule. The version history and the description live in Details & history, out
 * of the way of the design.
 *
 * Below the `lg` breakpoint the deck is one panel at a time behind a tab strip, because four
 * panels do not fit across a narrow window without scrolling sideways.
 */
import { useMemo, useState, type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import axios from 'axios'
import {
  ArrowDown,
  ArrowUp,
  Check,
  ChevronDown,
  Clock3,
  Columns3,
  FileSpreadsheet,
  FileText,
  Filter,
  GitBranch,
  History,
  Palette,
  Printer,
  Save,
  Sigma,
  Upload,
  X,
} from 'lucide-react'
import clsx from 'clsx'
import type { Loan } from '@/types'
import { printPdfBlob } from '@/components/shared/printPdfBlob'
import api from '@/services/api'
import { pipelineExportSettingsService } from '@/services/pipelineExportSettings'
import { pipelineViewsService } from '@/services/pipelineViews'
import type { ColumnId, FilterState, ReportConfig, SavedView, ViewAclUpdate } from './types'
import {
  DEFAULT_COLUMNS,
  PAPER_SIZES,
  maxPdfColumns,
  normalizeReportConfig,
  pdfColumnCapMessage,
} from './types'
import { PipelineReportScheduleModal } from './PipelineReportScheduleModal'
import { apiErrorMessage, ghostBtn, goldBtn } from './studioUi'
import { COLUMN_DEFS, COLUMN_MAP } from './columns'
import { applyLoanFilters } from './helpers'
import { applyReportFilters, isActiveColumnFilter, sortLoans } from './reporting'
import { DEMO_EXPORT_NOTICE, markDemoFilename, markDemoSubtitle, markDemoTitle } from '@/config/demoExportMark'
import { DEFAULT_REPORT_TITLE } from './reportExport'
import { buildDesignedViewGrid } from './reportLayout'
import { isDesignDirty } from './reportDirty'
import { DEFAULT_VIEW_SORT, filterViewLoans, viewFilters } from './savedViewExport'
import { ReportPreview } from './ReportPreview'
import { SavedReportsPanel } from './SavedReportsPanel'
import { ColumnsPanel } from './studio/ColumnsPanel'
import { DetailsDialog } from './studio/DetailsDialog'
import { FinishDialog } from './studio/FinishDialog'
import { FiltersPanel } from './studio/FiltersPanel'
import { StylePanel } from './studio/StylePanel'
import { libraryFolders } from './studio/libraryFolders'
import { TotalsPanel } from './studio/TotalsPanel'
import { PopoverPanel } from './studio/controls'
import { useDismiss } from './studio/controlStyles'

async function blobDetail(blob: unknown): Promise<string | null> {
  if (!(blob instanceof Blob)) return null
  try {
    const parsed = JSON.parse(await blob.text())
    return typeof parsed?.detail === 'string' ? parsed.detail : null
  } catch {
    return null
  }
}

function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  link.remove()
  setTimeout(() => URL.revokeObjectURL(url), 30_000)
}

type DeckPanelId = 'columns' | 'filters' | 'totals' | 'style'

/** The deck's panels, in reading order. Below `lg` these are the tab strip. */
const DECK_PANELS: Array<{ id: DeckPanelId; label: string; icon: typeof Columns3 }> = [
  { id: 'columns', label: 'Columns', icon: Columns3 },
  { id: 'filters', label: 'Filters', icon: Filter },
  { id: 'totals', label: 'Totals & calculations', icon: Sigma },
  { id: 'style', label: 'Style & paper', icon: Palette },
]

export function ReportStudio({
  view,
  allViews,
  loans,
  draftColumns,
  draftFilters,
  draftSort,
  assetClasses,
  assignees,
  years,
  onClose,
  onSave,
  onRestore,
  onPublish,
  onUpdateAcl,
  onApply,
  onSelectView,
  onSaveAsNew,
  onInvalidated,
  viewsLoading,
  viewsError,
  saving,
  headerTools,
  demoData = false,
}: {
  view: SavedView
  allViews: SavedView[]
  loans: Loan[]
  draftColumns: ColumnId[]
  draftFilters: FilterState
  draftSort: { id: ColumnId; direction: 'asc' | 'desc' }
  assetClasses: string[]
  assignees: string[]
  years: string[]
  onClose: () => void
  onSave: (view: SavedView, message: string) => Promise<void>
  onRestore: (view: SavedView, version: number, note: string) => Promise<void>
  onPublish: (view: SavedView) => Promise<void>
  onUpdateAcl: (view: SavedView, acl: ViewAclUpdate) => Promise<void>
  onApply: (columns: ColumnId[], filters: FilterState) => void
  onSelectView: (view: SavedView) => void
  onSaveAsNew: (name: string, columns: ColumnId[], filters: FilterState) => Promise<void>
  onInvalidated: () => Promise<void>
  viewsLoading: boolean
  viewsError: boolean
  saving: boolean
  /** Host-owned controls placed beside Print/Excel/PDF. The Reporting page
   * uses this slot for its Pipeline toolbar template and format defaults. */
  headerTools?: ReactNode
  /** The rows are the illustrative sample portfolio (demo mode is on). Every export
   * then says so — in its title, under it, and in its filename — instead of
   * leaving as a file that reads like the desk's own pipeline. */
  demoData?: boolean
}) {
  const isLocal = Boolean(view.isLocal)

  const [deckPanel, setDeckPanel] = useState<DeckPanelId>('columns')
  const [draftName, setDraftName] = useState(view.name)
  const [draftDescription, setDraftDescription] = useState(view.description)
  const [draftCols, setDraftCols] = useState<ColumnId[]>(
    draftColumns.length ? [...draftColumns] : [...DEFAULT_COLUMNS],
  )
  const [baseFilters, setBaseFilters] = useState<FilterState>(() => {
    const { report: _report, ...rest } = draftFilters
    return rest
  })
  // The studio opens on what is currently on screen: the grid's column order,
  // its active filters, and its active sort. A previously saved report design
  // supplies everything the screen does not express (style, totals, grouping,
  // column filters) - and the sort too while the grid's sort is still its
  // opening default. A sort the person changed is theirs and wins; an untouched
  // default must not overwrite the saved one the moment the report is opened
  // and saved again.
  const [config, setConfig] = useState<ReportConfig>(() => {
    const saved = view.filters?.report ? normalizeReportConfig(view.filters.report) : null
    const liveIsDefault =
      draftSort.id === DEFAULT_VIEW_SORT.id && draftSort.direction === DEFAULT_VIEW_SORT.direction
    const sort = saved && liveIsDefault ? { id: saved.sortId, direction: saved.sortDir } : draftSort
    return {
      ...normalizeReportConfig(draftFilters.report ?? view.filters?.report),
      sortId: sort.id,
      sortDir: sort.direction,
    }
  })

  const [note, setNote] = useState('')
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false)
  const [finishOpen, setFinishOpen] = useState(false)
  const [detailsOpen, setDetailsOpen] = useState(false)
  const [libraryOpen, setLibraryOpen] = useState(false)
  const libraryRef = useDismiss<HTMLDivElement>(libraryOpen, () => setLibraryOpen(false))
  const [exportBusy, setExportBusy] = useState<string | null>(null)
  const [exportError, setExportError] = useState<string | null>(null)
  // A save the server refused (an unknown column id, no edit access, a dropped
  // connection) says so here. It used to reject unseen, leaving a Save button
  // that did nothing and no word why.
  const [actionError, setActionError] = useState<string | null>(null)

  const isDraft = String(view.status).toLowerCase() === 'draft'

  // Full schedule management (create/edit/pause/delete/run-now) lives in
  // PipelineReportScheduleModal; this is only the count for the Finish
  // dialog's summary, and it shares a cache with the modal's own identical query.
  const schedulesQuery = useQuery({
    queryKey: ['pipeline-view-schedules', view.id] as const,
    queryFn: () => pipelineViewsService.listSchedules(view.id),
    enabled: !isLocal,
  })

  const exportSettingsQuery = useQuery({
    queryKey: ['pipeline-export-settings'] as const,
    queryFn: pipelineExportSettingsService.get,
  })
  const pdfColumnCaps = exportSettingsQuery.data?.pdfColumnCaps

  const renderableCols = useMemo(
    () => draftCols.filter((id) => COLUMN_MAP.has(id)),
    [draftCols],
  )

  const composedFilters = useMemo<FilterState>(
    () => ({ ...baseFilters, report: config }),
    [baseFilters, config],
  )

  const rows = useMemo(
    () =>
      sortLoans(
        applyReportFilters(applyLoanFilters(loans, baseFilters), config),
        config.sortId,
        config.sortDir,
      ),
    [loans, baseFilters, config],
  )

  const enumOptions = useMemo(() => {
    const map = new Map<ColumnId, string[]>()
    for (const def of COLUMN_DEFS) {
      if (def.kind !== 'enum') continue
      map.set(
        def.id,
        [...new Set(loans.map((loan) => def.display(loan)))].sort((a, b) => a.localeCompare(b)),
      )
    }
    return map
  }, [loans])

  const columnLabels = useMemo(() => new Map(COLUMN_DEFS.map((column) => [column.id, column.label])), [])
  const folders = useMemo(() => libraryFolders(allViews), [allViews])

  const busy = saving

  const activeFilterCount =
    Object.entries(config.columnFilters).filter(([, filter]) => isActiveColumnFilter(filter)).length +
    (config.dateFrom || config.dateTo ? 1 : 0)

  const paper = PAPER_SIZES.find((option) => option.id === config.paperSize) ?? PAPER_SIZES[1]

  const filenameBase = `${(draftName || 'pipeline').replace(/\s+/g, '_')}_v${view.version}_report`
  const displayReportTitle =
    config.title.trim() ||
    (draftName.trim() && draftName.trim().toLowerCase() !== 'local workspace'
      ? draftName.trim()
      : DEFAULT_REPORT_TITLE)

  // Unsaved design edits relative to the report as stored - guards report
  // switching, and scheduling (a schedule runs the saved version). Compared
  // normalised: a view never designed stores no `report`, which is the default
  // design, not a difference.
  const dirty = useMemo(
    () =>
      isDesignDirty(
        { columns: draftCols, filters: composedFilters },
        { columns: view.columns, filters: view.filters },
      ),
    [draftCols, composedFilters, view.columns, view.filters],
  )

  // What a schedule will actually run: the SAVED version, whatever the draft says.
  const savedSummary = useMemo(() => {
    const filters = viewFilters(view)
    const saved = filters.report ? normalizeReportConfig(filters.report) : null
    const names = view.columns.map((id) => columnLabels.get(id)).filter((name): name is string => Boolean(name))
    return {
      rowCount: filterViewLoans(loans, filters).length,
      columnNames: names,
      filterCount: saved
        ? Object.values(saved.columnFilters).filter((filter) => isActiveColumnFilter(filter)).length +
          (saved.dateFrom || saved.dateTo ? 1 : 0)
        : 0,
      groupByLabel: saved?.groupBy
        ? saved.groupBy === 'stage'
          ? 'Stage'
          : columnLabels.get(saved.groupBy) ?? saved.groupBy
        : null,
    }
  }, [view, loans, columnLabels])

  const toggleColumn = (id: ColumnId) => {
    setDraftCols((current) => {
      if (current.includes(id)) {
        if (current.length === 1) return current
        return current.filter((columnId) => columnId !== id)
      }
      return [...current, id]
    })
  }

  /** The columns the search picker adds, at the end, in the order it was given. */
  const addColumns = (ids: ColumnId[]) => {
    setDraftCols((current) => [...current, ...ids.filter((id, index) => !current.includes(id) && ids.indexOf(id) === index)])
  }

  const moveColumn = (id: ColumnId, delta: number) => {
    setDraftCols((current) => {
      const index = current.indexOf(id)
      const target = index + delta
      if (index < 0 || target < 0 || target >= current.length) return current
      const next = [...current]
      next.splice(index, 1)
      next.splice(target, 0, id)
      return next
    })
  }

  const reorderColumn = (source: ColumnId, target: ColumnId) => {
    if (source === target) return
    setDraftCols((current) => {
      const next = current.filter((columnId) => columnId !== source)
      const targetIndex = next.indexOf(target)
      if (targetIndex < 0) return current
      next.splice(targetIndex, 0, source)
      return next
    })
  }

  const onPreviewSort = (id: ColumnId) =>
    setConfig((current) =>
      current.sortId === id
        ? { ...current, sortDir: current.sortDir === 'asc' ? 'desc' : 'asc' }
        : { ...current, sortId: id, sortDir: 'desc' },
    )

  // The pipeline report exports without a second approver on every surface
  // (register I6 carve-out, owner decision 25 Sep 2026), as does the Deal
  // Locator Excel export. Database Console raw-table exports and whole-book
  // reports keep the Export Vault.
  const runExport = async (kind: 'xlsx' | 'pdf' | 'print') => {
    if (kind !== 'xlsx' && renderableCols.length > maxPdfColumns(config.paperSize, pdfColumnCaps)) {
      setExportError(pdfColumnCapMessage(config.paperSize, renderableCols.length, pdfColumnCaps))
      return
    }
    setExportBusy(kind)
    setExportError(null)
    try {
      // One path for every build and both formats: the server renders (and
      // archives) the file from the design. Excel used to be built in the
      // browser in development and by the server in production, from a request
      // that carried headers and text only - so production Excel lost the
      // grouping, the totals and the numbers the preview showed.
      // Demo rows leave marked: the title, the notice under it and the filename
      // all say the figures are the illustrative sample (config/demoExportMark.ts).
      const exportTitle = markDemoTitle(displayReportTitle, demoData)
      const exportConfig = demoData
        ? { ...config, title: exportTitle, subtitle: markDemoSubtitle(config.subtitle, true) }
        : config
      const grid = buildDesignedViewGrid(rows, draftCols, exportTitle, exportConfig)
      const format = kind === 'xlsx' ? 'xlsx' : 'pdf'
      const res = await api.post(
        '/reports/generate',
        {
          template: view.id === 'current-status' ? 'current_status' : 'pipeline_summary',
          filters: {
            stage: baseFilters.stage !== 'All' ? baseFilters.stage : undefined,
            asset_type: baseFilters.assetClass !== 'All' ? baseFilters.assetClass : undefined,
            viewId: view.id,
            viewName: exportTitle,
            viewVersion: view.version,
            columns: draftCols,
            query: composedFilters,
            // The design itself (title, subtitle, palette, grouping, totals,
            // paper), read by the server beside the matrix. Demo rows carry the
            // marked title and subtitle (config/demoExportMark.ts).
            report: exportConfig,
            viewGrid: grid,
          },
          format,
        },
        { responseType: 'blob' },
      )
      const blob = res.data as Blob
      if (kind === 'print') {
        // window.open with 'noopener' returns null by specification, so the
        // old path reported a blocked pop-up on every click. Print from an
        // offscreen frame; save the file only if the engine will not print.
        printPdfBlob(blob, {
          onFallback: () => {
            saveBlob(blob, `${markDemoFilename(filenameBase, demoData)}.pdf`)
            setExportError('This browser would not print the PDF directly — it was downloaded instead; open it to print.')
          },
        })
      } else {
        saveBlob(blob, `${markDemoFilename(filenameBase, demoData)}.${format}`)
      }
    } catch (err) {
      if (axios.isAxiosError(err)) {
        const detail = await blobDetail(err.response?.data)
        const status = err.response?.status
        setExportError(
          status
            ? `Failed to export ${kind.toUpperCase()} (HTTP ${status})${detail ? `: ${detail}` : ''}`
            : apiErrorMessage(err, `Failed to export ${kind.toUpperCase()}`),
        )
      } else {
        setExportError(apiErrorMessage(err, `Failed to export ${kind.toUpperCase()}`))
      }
    } finally {
      setExportBusy(null)
    }
  }

  /** Runs a write; a refusal is shown in the banner and re-thrown for the caller to stop on. */
  const guarded = async <T,>(write: () => Promise<T>): Promise<T> => {
    setActionError(null)
    try {
      return await write()
    } catch (err) {
      setActionError(apiErrorMessage(err, 'The change could not be saved'))
      throw err
    }
  }

  /** Saves the draft as a new version. Rejects when the server refuses, having said why. */
  const saveVersion = () =>
    guarded(() =>
      onSave(
        {
          ...view,
          name: draftName,
          description: draftDescription,
          columns: draftCols,
          filters: composedFilters,
        },
        note.trim(),
      ),
    )

  const schedules = schedulesQuery.data ?? []
  const scheduleSummary = schedulesQuery.isLoading
    ? 'Loading this report’s schedules…'
    : schedulesQuery.isError
      ? 'Could not load schedules from the API.'
      : schedules.length === 0
        ? 'No schedules yet for this report.'
        : `${schedules.filter((s) => s.isActive).length} active of ${schedules.length} configured.`

  const exportDisabled = exportBusy != null || rows.length === 0
  const pdfColumnLimit = maxPdfColumns(config.paperSize, pdfColumnCaps)
  const pdfOverColumnCap = renderableCols.length > pdfColumnLimit
  const pdfExportDisabled = exportDisabled || pdfOverColumnCap
  const pdfCapMessage = pdfOverColumnCap
    ? pdfColumnCapMessage(config.paperSize, renderableCols.length, pdfColumnCaps)
    : null

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-1.5">
        <div
          className="flex h-[96vh] w-[min(1920px,98vw)] max-w-[98vw] flex-col overflow-hidden rounded-xl border border-amber-300/15 bg-[#0d0d0d] shadow-2xl"
          data-testid="report-studio"
        >
          <div className="flex shrink-0 flex-wrap items-start justify-between gap-x-3 gap-y-2 border-b border-amber-300/10 px-5 py-2.5">
            <div className="min-w-0 flex-1 basis-72">
              <div className="flex items-baseline gap-3">
                <span className="shrink-0 text-[10px] uppercase tracking-[0.16em] text-amber-200/70">Report studio</span>
                <input
                  value={draftName}
                  onChange={(event) => setDraftName(event.target.value)}
                  maxLength={160}
                  aria-label="Report name"
                  className="-ml-1 min-w-0 flex-1 truncate rounded border border-transparent bg-transparent px-1 text-lg text-stone-100 outline-none hover:border-white/10 focus:border-amber-300/40"
                />
              </div>
              <div className="text-[12px] text-stone-500">
                {isLocal
                  ? 'Pipeline Report draft — saved-view service unavailable'
                  : `Current v${view.version} · ${view.ownerEmail} · ${view.status}`}
                {' · '}
                {rows.length} row{rows.length === 1 ? '' : 's'} · {draftCols.length} columns
                {activeFilterCount > 0
                  ? ` · ${activeFilterCount} report filter${activeFilterCount === 1 ? '' : 's'}`
                  : ''}
                {' · '}
                {paper.label}
              </div>
            </div>
            <div className="flex flex-wrap items-center justify-end gap-2">
              <div ref={libraryRef} className="relative">
                <button
                  type="button"
                  onClick={() => setLibraryOpen((open) => !open)}
                  aria-expanded={libraryOpen}
                  className={ghostBtn}
                  title="Open another saved report, or save this design as a new one"
                >
                  Saved reports <ChevronDown className="h-3 w-3" />
                </button>
                {libraryOpen ? (
                  <PopoverPanel label="Saved reports" align="right" className="flex h-[min(30rem,68vh)] overflow-hidden p-0">
                    <SavedReportsPanel
                      views={allViews}
                      activeViewId={view.id}
                      loading={viewsLoading}
                      error={viewsError || isLocal}
                      dirty={dirty}
                      saving={saving}
                      collapsed={false}
                      onToggleCollapsed={() => setLibraryOpen(false)}
                      onSelect={(next) => {
                        setLibraryOpen(false)
                        onSelectView(next)
                      }}
                      onSaveAsNew={(name, folder) =>
                        guarded(() => onSaveAsNew(name, draftCols, { ...composedFilters, report: { ...config, folder } }))
                      }
                    />
                  </PopoverPanel>
                ) : null}
              </div>
              <button
                type="button"
                onClick={() => setDetailsOpen(true)}
                disabled={isLocal}
                className={ghostBtn}
                title={isLocal ? 'Saved-view service unavailable' : 'The description, and every version of this report'}
              >
                <History className="h-3 w-3" /> Details &amp; history
              </button>
              {headerTools ? (
                <div data-testid="report-studio-header-tools" className="flex flex-wrap items-center justify-end gap-2">
                  {headerTools}
                </div>
              ) : null}
              {demoData ? (
                <span
                  data-testid="report-studio-demo-notice"
                  className="rounded border border-amber-300/40 bg-amber-400/10 px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-amber-200"
                  title="These rows are the illustrative sample portfolio. Print, Excel and PDF will carry this mark."
                >
                  {DEMO_EXPORT_NOTICE}
                </span>
              ) : null}
              <button
                type="button"
                disabled={pdfExportDisabled}
                onClick={() => void runExport('print')}
                className={ghostBtn}
                title={pdfCapMessage ?? 'Open the report PDF and print it'}
              >
                <Printer className="h-3 w-3" />
                {exportBusy === 'print' ? 'Preparing…' : 'Print'}
              </button>
              <button
                type="button"
                disabled={exportDisabled}
                onClick={() => void runExport('xlsx')}
                className={ghostBtn}
                title="Download a styled Excel workbook matching the preview"
              >
                <FileSpreadsheet className="h-3 w-3" />
                {exportBusy === 'xlsx' ? 'Building…' : 'Excel'}
              </button>
              <button
                type="button"
                disabled={pdfExportDisabled}
                onClick={() => void runExport('pdf')}
                className={ghostBtn}
                title={pdfCapMessage ?? 'Download the report as a PDF'}
              >
                <FileText className="h-3 w-3" />
                {exportBusy === 'pdf' ? 'Generating…' : 'PDF'}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="rounded p-1 text-stone-500 hover:bg-white/5 hover:text-stone-200"
                aria-label="Close report studio"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
          {pdfCapMessage ? (
            <div
              role="status"
              data-testid="report-studio-pdf-column-cap"
              className="border-b border-amber-300/20 bg-amber-400/[0.06] px-5 py-1.5 text-[11px] text-amber-200"
            >
              {pdfCapMessage}
            </div>
          ) : null}
          {exportError || actionError ? (
            <div
              role="alert"
              className="border-b border-red-400/20 bg-red-400/[0.06] px-5 py-1.5 text-[11px] text-red-300"
            >
              {exportError ?? actionError}
            </div>
          ) : null}

          <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
            {/*
              The design deck: Columns, Filters, Totals & calculations, Style & paper, side by
              side. It used to be five steps behind a tab strip, each a scrolling pane, so no two
              controls that shape one report could be seen together and the live report got what
              was left. Four panels across the window put every control on screen at once; the
              report keeps the rest of the height and the full width.

              No overflow on this region: the panels' popovers (the column search, a column
              filter, the colours) open downward over the report and a scrolling ancestor would
              clip them.
            */}
            <div data-testid="report-studio-editor-region" className="shrink-0 border-b border-amber-300/10 bg-black/20">
              <div role="tablist" aria-label="Design panels" className="flex border-b border-white/[0.05] lg:hidden">
                {DECK_PANELS.map((panel) => {
                  const Icon = panel.icon
                  const selected = deckPanel === panel.id
                  return (
                    <button
                      key={panel.id}
                      type="button"
                      role="tab"
                      aria-selected={selected}
                      onClick={() => setDeckPanel(panel.id)}
                      className={clsx(
                        'flex flex-1 items-center justify-center gap-1.5 px-2 py-2 text-[10px] uppercase tracking-[0.1em] transition',
                        selected
                          ? 'border-b-2 border-amber-300 bg-amber-300/[0.05] text-amber-200'
                          : 'border-b-2 border-transparent text-stone-500 hover:text-stone-300',
                      )}
                    >
                      <Icon className="h-3.5 w-3.5" />
                      {panel.label}
                    </button>
                  )
                })}
              </div>
              <div className="grid lg:grid-cols-4">
                <ColumnsPanel
                  active={deckPanel === 'columns'}
                  columns={draftCols}
                  onAdd={addColumns}
                  onRemove={toggleColumn}
                  onMove={moveColumn}
                  onReorder={reorderColumn}
                  onMatchScreen={() => {
                    setDraftCols(draftColumns.length ? [...draftColumns] : [...DEFAULT_COLUMNS])
                    setConfig((current) => ({
                      ...current,
                      sortId: draftSort.id,
                      sortDir: draftSort.direction,
                    }))
                  }}
                  onResetDefault={() => setDraftCols([...DEFAULT_COLUMNS])}
                />
                <FiltersPanel
                  active={deckPanel === 'filters'}
                  config={config}
                  setConfig={setConfig}
                  baseFilters={baseFilters}
                  setBaseFilters={setBaseFilters}
                  assetClasses={assetClasses}
                  assignees={assignees}
                  years={years}
                  enumOptions={enumOptions}
                />
                <TotalsPanel active={deckPanel === 'totals'} config={config} setConfig={setConfig} columns={draftCols} />
                <StylePanel
                  active={deckPanel === 'style'}
                  config={config}
                  setConfig={setConfig}
                  reportName={draftName}
                  folders={folders}
                />
              </div>
            </div>

            <div
              className="flex min-h-[8rem] min-w-0 flex-1 flex-col gap-1.5 p-2.5"
              data-testid="report-studio-preview-region"
            >
              <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 text-[10px] text-stone-500">
                <span>
                  Sorted by {columnLabels.get(config.sortId) ?? config.sortId}{' '}
                  {config.sortDir === 'asc' ? (
                    <ArrowUp className="inline h-2.5 w-2.5" />
                  ) : (
                    <ArrowDown className="inline h-2.5 w-2.5" />
                  )}{' '}
                  — click a header to re-sort
                </span>
                <span data-testid="report-studio-paper-note">
                  Prints on {paper.label} · landscape · {paper.dimensions}
                  {' · '}columns auto-fit to content{config.style.wrapText ? ' · word wrap on' : ''}
                </span>
              </div>
              <ReportPreview
                editableResponsibleParty={!demoData}
                loans={rows}
                columns={draftCols}
                config={config}
                onSort={onPreviewSort}
                reportName={displayReportTitle}
              />
            </div>
          </div>

          <div
            data-testid="report-studio-action-bar"
            className="flex shrink-0 flex-wrap items-center justify-between gap-2 border-t border-amber-300/10 px-5 py-2.5"
          >
            {/*
              No "Branch".

              It made a copy called "<name> (branch)" and left both in the
              library, which is how a desk ends up with four Active Pipelines and
              no way to tell which one anybody is reading. Starting from an
              existing report is now a deliberate choice at the point of
              creation — Create New Report asks whether to change one that exists
              or start fresh — rather than a button whose meaning has to be
              guessed from a version-control metaphor.
            */}
            <div className="flex flex-wrap items-center gap-3">
              {isDraft && !isLocal ? (
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => void guarded(() => onPublish(view)).catch(() => undefined)}
                  className="inline-flex items-center gap-1.5 rounded border border-amber-300/30 px-3 py-1.5 text-[11px] text-amber-100 hover:bg-amber-300/10 disabled:opacity-60"
                >
                  <Upload className="h-3.5 w-3.5" /> Publish
                </button>
              ) : null}
              <span className="inline-flex items-center gap-1 text-[10px] text-stone-600">
                <Clock3 className="h-3 w-3" />
                {dirty ? 'Unsaved design changes' : `Saved as v${view.version}`}
              </span>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <input
                value={note}
                onChange={(event) => setNote(event.target.value)}
                placeholder="Change rationale (required to save a version)"
                className="w-72 rounded border border-stone-700 bg-black/40 px-3 py-1.5 text-[11px] text-stone-100 outline-none focus:border-amber-300/40"
                disabled={isLocal}
              />
              <button
                type="button"
                onClick={() => onApply(draftCols, composedFilters)}
                className="inline-flex items-center gap-1.5 rounded border border-amber-300/30 px-3 py-1.5 text-[11px] text-amber-100 hover:bg-amber-300/10"
                title="Use this design in the workspace without saving a version"
              >
                <Check className="h-3.5 w-3.5" /> Apply to workspace
              </button>
              <button
                type="button"
                disabled={busy || !note.trim() || isLocal}
                onClick={() => void saveVersion().catch(() => undefined)}
                className="inline-flex items-center gap-1.5 rounded border border-amber-300/30 px-3 py-1.5 text-[11px] text-amber-100 hover:bg-amber-300/10 disabled:opacity-50"
                title={isLocal ? 'Saved-view service unavailable' : undefined}
              >
                {saving ? <GitBranch className="h-3.5 w-3.5 animate-pulse" /> : <Save className="h-3.5 w-3.5" />}
                Save new version
              </button>
              <button
                type="button"
                disabled={isLocal}
                onClick={() => setFinishOpen(true)}
                className={goldBtn}
                title={
                  isLocal
                    ? 'Saved-view service unavailable'
                    : 'Keep it private, share it with a colleague, or send it on a schedule'
                }
              >
                <Check className="h-3.5 w-3.5" /> Finish
              </button>
            </div>
          </div>
        </div>
      </div>

      {finishOpen && !isLocal ? (
        <FinishDialog
          view={view}
          dirty={dirty}
          busy={busy}
          note={note}
          onNoteChange={setNote}
          onSaveVersion={saveVersion}
          onUpdateAcl={(acl) => guarded(() => onUpdateAcl(view, acl))}
          scheduleSummary={scheduleSummary}
          onOpenSchedule={() => {
            setFinishOpen(false)
            setScheduleModalOpen(true)
          }}
          onClose={() => setFinishOpen(false)}
        />
      ) : null}

      {detailsOpen && !isLocal ? (
        <DetailsDialog
          view={view}
          description={draftDescription}
          onDescriptionChange={setDraftDescription}
          draftColumns={draftCols}
          draftFilters={composedFilters}
          columnLabels={columnLabels}
          busy={busy}
          onRestore={(version, restoreNote) => guarded(() => onRestore(view, version, restoreNote))}
          onClose={() => setDetailsOpen(false)}
        />
      ) : null}

      {scheduleModalOpen && !isLocal ? (
        <PipelineReportScheduleModal
          view={view}
          rowCount={savedSummary.rowCount}
          columnCount={savedSummary.columnNames.length}
          columnNames={savedSummary.columnNames}
          activeFilterCount={savedSummary.filterCount}
          groupByLabel={savedSummary.groupByLabel}
          unsaved={dirty}
          onInvalidated={onInvalidated}
          onClose={() => setScheduleModalOpen(false)}
        />
      ) : null}
    </>
  )
}

